import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashCompact from 'lodash/compact';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS } from 'client/constants/screenIds';

import {
  hasRoleAdmin,
  hasRolePartnerAdmin,
  hasRolePartnerAccounting,
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER,
} from 'common/utility/userRoles';
import { PMS_NAMES_WITH_PICKER } from 'common/propertyManagementSoftwareConstants';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrNavigationButton from 'client/components/SrNavigationButton/SrNavigationButton';

import UtilityPartnerActions from 'client/actions/UtilityPartner';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';
import { isHomebuyerPartner } from 'common/utility/helpers';
import { canManagePartnerApiKeys } from 'common/utility/partnerApiKeys';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS)
@withScrollToTop
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  utilityUser: ['utilityUser'],
}))
class UtilityPartnerSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
  });

  static fetchData(match, inject) {
    return UtilityPartnerActions.getBySlug(inject, {
      slug: match.params.partnerSlug,
      forceRefresh: true,
    });
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  render() {
    const { utilityUser, utilityPartner, isTransitioning } = this.props;

    const isAdmin = hasRoleAdmin(utilityUser.roles);

    const isPartnerAccounting =
      (hasRolePartnerAccounting(utilityUser.roles) ||
        hasRolePartnerAdmin(utilityUser.roles) ||
        isAdmin) &&
      utilityPartner.experienceConfiguration.detailedModeActive;

    if (isTransitioning && !utilityPartner) {
      return null;
    }

    if (!utilityPartner) {
      return <Redirect to="/" />;
    }

    const isHomebuyer = isHomebuyerPartner(utilityPartner);

    // The Integrations subpage only has content for picker-enabled PMS partners (the
    // housing-types sync picker); everyone else keeps a deep-link empty state but no nav
    // card. Same detection as UtilityPartnerSettingsIntegrationsPage.
    const hasConfigurableIntegration = PMS_NAMES_WITH_PICKER.includes(
      utilityPartner.propertyManagementSoftware?.name,
    );

    const navigationButtons = lodashCompact([
      {
        color: '#132630',
        iconUrl: '/images/tab-leads-white.svg',
        iconSize: 25,
        to: `/utility-profit/${utilityPartner.slug}/settings/team`,
        title: 'Team Members',
        subtitle: 'Manage your team members.',
      },
      isPartnerAccounting && {
        color: '#00A238',
        iconUrl: '/images/dollar-white.svg',
        to: `/utility-profit/${utilityPartner.slug}/settings/banks`,
        title: 'Payouts',
        subtitle: 'Setup collection method',
      },
      !isHomebuyer && {
        color: '#AD00AD',
        iconUrl: '/images/checkmark-icon-thick-white.svg',
        iconSize: 25,
        to: `/utility-profit/${utilityPartner.slug}/settings/tenant`,
        title: 'Tenant Settings',
        subtitle: 'Customize your tenant experience.',
      },
      hasConfigurableIntegration && {
        // @blue-bright — matches the SrCard blue bullet accent on the Integrations subpage.
        color: '#007AFF',
        iconUrl: '/images/refresh-white.svg',
        iconSize: 20,
        to: `/utility-profit/${utilityPartner.slug}/settings/integrations`,
        title: 'Integrations',
        subtitle: 'Manage your PM software sync.',
      },
      canManagePartnerApiKeys(utilityUser, utilityPartner) && {
        color: '#132630',
        iconUrl: '/images/checkmark-icon-thick-white.svg',
        iconSize: 24,
        to: `/utility-profit/${utilityPartner.slug}/settings/api-keys`,
        title: 'API Keys',
        subtitle: 'Manage keys for your integrations.',
      },
    ]);

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}`}
        navbarTitle={utilityPartner.name}
        navbarImage={utilityPartner.logoUrl}
        redirectToLastLocation={false}
        pageHeader="Account Settings"
      >
        {navigationButtons.map((button) => (
          <React.Fragment key={button.title}>
            <SrNavigationButton
              color={button.color}
              iconUrl={button.iconUrl}
              iconSize={button.iconSize}
              to={button.to}
              title={button.title}
              subtitle={button.subtitle}
            />
            <SrBox marginTop={10} />
          </React.Fragment>
        ))}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsPage.displayName = 'UtilityPartnerSettingsPage';

export default UtilityPartnerSettingsPage;
